import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import moment from 'moment'
import { useNavigate } from 'react-router-dom'

// components
import AppCard from '../../components/AppCard'
import AppCardHeader from 'src/components/card/AppCardHeader'
import AppForm from '../../components/form/form-employee/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import FormFileThumbnail from 'src/components/form/general-input-form/FormFileThumbnail'
import AppModalConfirmation from '../../components/modal/AppModalConfirmation'
import AppModalFilePreview from 'src/components/modal/AppModalFilePreview'

// helpers
import Axios from '../../helpers/axios'
import { 
  singleCountriesOptionsSelect, 
  singleGenderOptionSelect, 
  singleStatusContractOptionSelect, 
  singleDegreeOptionsSelect,
  singleProjectsOptionsSelect,
  singleTypeSalaryDetailOptionSelect
} from '../../helpers/format-options'
import useToastMessage from 'src/helpers/toast-message'

const AddEmployee = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess } = useToastMessage();

  const isLoading = useSelector((state) => state.isLoading)

  const [countryOptions, setCountryOptions] = useState('')
  const [country, setCountry] = useState({})

  const [degreeOptions, setDegreeOptions] = useState('')
  const [projectOptions, setProjectOptions] = useState('')

  const [gender, setGender] = useState(null)
  const [statusContract, setStatusContract] = useState(null)
  const [projects, setProjects] = useState(null)

  const [showDeleteItem, setShowDeleteItem] = useState({ show: false, item: {} })
  const [showConfirmAddAttachments, setShowConfirmAddAttachments] = useState({ show: false, item: {} })
  const [showModalConfirm, setShowModalConfirm] = useState({ show: false })

  const [openFilePreview, setOpenFilePreview] = useState(false)

  const lastAccordionSalaryDetailRef = useRef(null);
  const [shouldScroll, setShouldScroll] = useState(false);

  const [activeAccordionKey, setActiveAccordionKey] = useState(null)

  const formik = useFormik({
    initialValues: {
      employee_first_name: '',
      employee_last_name: '',
      employee_address: '',
      employee_id_card_number: '',
      phone_number: '',
      country_id: '',
      is_contract_base: false,
      employee_contract_start_date: '',
      employee_contract_end_date: '',
      employee_gender: '',
      employee_job_title: '',
      is_active: false,
      employee_no: '',
      employee_email_address: '',
      employee_place_of_birth: '',
      employee_date_of_birth: '',
      employee_status: '',
      employee_bpjs_kis_no: '',
      employee_register_date: '',
      employee_educations: [
        {
          degree_type: null,
          field_of_study: '',
          institution_name: '',
          graduation_year: '',
          country_value: null
        }
      ],
      employee_projects: [],
      employee_accounts: [
        {
          account_number: '',
          account_name: '',
          bank_name: '',
          bank_branch: '',
          is_main_account: false
        }
      ],
      attachments: [],
      employee_notes: '',
      employee_salary_details: [
        {
          detail_name: 'Gaji Pokok',
          detail_value: '',
          is_deduction: false
        },
        {
          detail_name: 'Tunjangan Jabatan',
          detail_value: '',
          is_deduction: false
        },
        {
          detail_name: 'Tunjangan Transport',
          detail_value: '',
          is_deduction: false
        },
        {
          detail_name: 'Tunjangan Komunikasi',
          detail_value: '',
          is_deduction: false
        },
        {
          detail_name: 'Tunjangan Konsumsi',
          detail_value: '',
          is_deduction: false
        },
        {
          detail_name: 'Ekstra Insentif',
          detail_value: '',
          is_deduction: false
        }
      ],
      npwp_image: [],
    },
    validationSchema: Yup.object().shape({
      employee_first_name: Yup.string()
        .required("Please enter first name."),
      employee_last_name: Yup.string()
        .required("Please enter last name."),
      employee_address: Yup.string()
        .required("Please enter address."),
      employee_id_card_number: Yup.string()
        .required("Please enter id card number."),
      phone_number: Yup.string()
        .required("Please enter contact number."),
      country_id: Yup.string().required("Please enter contact number country."),
      employee_contract_start_date: Yup.string()
        .when('is_contract_base', {
          is: true,
          then: () => Yup.string().required('Please select contract start date')
        }),
      employee_contract_end_date: Yup.string()
        .when('is_contract_base', {
          is: true,
          then: () => Yup.string().required('Please select contract end date')
        }),
      employee_email_address: Yup.string().required("Please enter email address."),
      employee_status: Yup.string().required("Please select status."),
      employee_register_date: Yup.string().required("Please select register date."),
      employee_job_title: Yup.string().required("Please enter job title/position."),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    fetchAllCountries()
    fetchAllDegree()
    fetchAllProject()
  }, []);

  useEffect(() => {
    const firstErrorKey = Object.keys(formik.errors)[0];
    
    if (firstErrorKey) {
      const errorElement = document.querySelector(`[id="${firstErrorKey}"]`);
      
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement.focus();
      }
    }
  }, [formik.isSubmitting])

  const fetchAllCountries = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/countries",
    })
      .then(({ data }) => {
        setCountryOptions(singleCountriesOptionsSelect(data))
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  };

  const fetchAllDegree = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/degree",
    })
      .then(({ data }) => {
        setDegreeOptions(singleDegreeOptionsSelect(data))
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const fetchAllProject = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/project/option/list",
    })
      .then(({ data }) => {
        setProjectOptions(singleProjectsOptionsSelect(data?.value))
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const handleOnDateChange = (date, name) => {
    formik.setValues((values) => ({
      ...values,
      [name]: date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : ''
    }), false)
  }

  const handleImagesOnDrop = (files) => {
    const attachments = []
    if (files[0] && typeof files[0] !== 'undefined') {
      attachments.push(files[0])
      formik.setValues((values) => ({
        ...values,
        npwp_image: attachments
      }), false)
    }
  }

  const handleImageAttachment = (e) => {
    e.preventDefault()
    const files = e.target.files
    const attachments = []
    if (files[0] && typeof files[0] !== 'undefined') {
      attachments.push(files[0])
      formik.setValues((values) => ({
        ...values,
        npwp_image: attachments
      }), false)
    }
  }

  const handleInputValue = (values) => {
    const formData = new FormData()
    formData.append('first_name', values.employee_first_name)
    formData.append('last_name', values.employee_last_name)
    formData.append('id_card_number', values.employee_id_card_number)
    formData.append('contact_number', values.phone_number)
    formData.append('address', values.employee_address)
    formData.append('country_id', values.country_id)
    formData.append('is_contract_base', values.is_contract_base)
    formData.append('is_active', values.is_active)
    formData.append('gender', values.employee_gender)
    formData.append('employee_no', values.employee_no)
    formData.append('email_address', values.employee_email_address)
    formData.append('place_of_birth', values.employee_place_of_birth)
    formData.append('date_of_birth', values.employee_date_of_birth)
    formData.append('status', values.employee_status)
    formData.append('bpjs_kis_no', values.employee_bpjs_kis_no)
    formData.append('register_date', values.employee_register_date)
    formData.append('notes', values.employee_notes)
    formData.append('job_title', values.employee_job_title)
    if (values.is_contract_base) {
      formData.append('contract_start_date', values.employee_contract_start_date)
      formData.append('contract_end_date', values.employee_contract_end_date)
    }
    if (values?.attachments?.length > 0) {
      for (let i = 0; i < values.attachments.length; i++) {
        formData.append('file', values.attachments[i])
      }
    }
    if (values?.employee_educations?.length > 0) {
      formData.append('educations', JSON.stringify(values.employee_educations))
    }
    if (values?.employee_projects?.length > 0) {
      formData.append('projects', JSON.stringify(values.employee_projects))
    }
    if (values?.employee_accounts?.length > 0) {
      formData.append('accounts', JSON.stringify(values.employee_accounts))
    }
    if (values?.employee_salary_details?.length > 0) {
      formData.append('salary_details', JSON.stringify(values.employee_salary_details))
    }
    if (values.npwp_image.length > 0) {
      formData.append('npwp', values.npwp_image[0])
    }

    return formData
  }

  const handlePost = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/tracker/v1/employee/create`,
      data: payload
    })
      .then(({ data }) => {
        if (data?.success) {
          showSuccess('New employee has been successfully created')
          navigate('/employee/list')
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handleCardHeaderButton = () => {
    setShowConfirmAddAttachments({ show: true, item: {} })
  }

  const handleFileInput = (values, type) => {
    const files = values

    if (files) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        [type]: [...prevValues[type], ...files]
      }), false)
    }

    setShowConfirmAddAttachments({ show: false, item: {} })
  }

  const handleRemoveFile = (index = 0, name) => {
    let type = name
    if (!name) {
      showDeleteItem.type ? type = showDeleteItem.type : type = 'npwp_image'
    }
    if (type == 'employee_attachments') {
      type = 'attachments'
    }
    formik.setValues((values) => ({
      ...values,
      [type]: values[type]?.filter((_, i) => i !== index)
    }), false)

    setShowDeleteItem({ show: false, type: '', item: {}, index: 0 })
  }

  const handleSubmit = () => {
    formik.handleSubmit()

    setShowModalConfirm({ show: false })
  }

  const handleSelectCountry = (data) => {
    formik.setValues((values) => ({
      ...values,
      country_id: data ? JSON.parse(data?.value).id : ''
    }), false)

    setCountry(data ? data?.value : null)
  }

  const handleSelectGender = (data) => {
    formik.setValues((values) => ({
      ...values,
      employee_gender: data ? data?.value : ''
    }), false)

    setGender(data ? data : null)
  }

  const handleSelectStatusContract = (data) => {
    formik.setValues((values) => ({
      ...values,
      employee_status: data ? data?.value : '',
      is_contract_base: data ? data?.value == 'full-time' ? false : true : false
    }))

    setStatusContract(data ? data : null)
  }

  const handleSelectProject = (data) => {
    formik.setValues((values) => ({
      ...values,
      employee_projects: data ? data : []
    }), false)

    setProjects(data ? data : [])
  }

  const handleAddEmployeeEducations = () => {
    formik.setValues((values) => ({
      ...values,
      employee_educations: [
        ...values.employee_educations,
        ...[
          {
            degree_type: null,
            field_of_study: '',
            institution_name: '',
            graduation_year: '',
            country_value: null
          }
        ]
      ]
    }), false)
  }

  const handleAddEmployeeAccounts = () => {
    formik.setValues((values) => ({
      ...values,
      employee_accounts: [
        ...values.employee_accounts,
        ...[
          {
            account_number: '',
            account_name: '',
            bank_name: '',
            bank_branch: '',
            is_main_account: false
          }
        ]
      ]
    }), false)
  }

  const handleAddEmployeeSalaryDetails = () => {
    setShouldScroll(true);
    formik.setValues((values) => {
      const updated = [
        ...values.employee_salary_details,
        {
          detail_name: '',
          detail_value: '',
          is_deduction: false
        }
      ]

      // Set accordion baru sebagai aktif
      setActiveAccordionKey(updated.length)

      return {
        ...values,
        employee_salary_details: updated
      }
    }, false)
  }

  const handleChangeEducation = (e, informationIndex) => {
    const name = e?.target?.name
    const value = e?.target?.value

    formik.setValues((values) => {
      const newArrayInformation = values.employee_educations.map((item, itemIndex) => {
        if (itemIndex == informationIndex) {
          return {
            ...item,
            [name]: value
          };
        } else {
          return item;
        }
      });

      return {
        ...values,
        employee_educations: [
          ...newArrayInformation,
        ]
      }
    }, false)
  }

  const handleChangeAccount = (e, informationIndex) => {
    const name = e?.target?.name || e?.target?.id
    const value = e?.target?.value
    const checked = e?.target?.checked

    formik.setValues((values) => {
      const newArrayInformation = values.employee_accounts.map((item, itemIndex) => {
        if (itemIndex == informationIndex) {
          return {
            ...item,
            [name]: name == 'is_main_account' ? checked : value
          };
        } else {
          if (name === 'is_main_account') {
            return {
              ...item,
              [name]: false
            }
          } else {
            return item;
          }
        }
      });

      return {
        ...values,
        employee_accounts: [
          ...newArrayInformation,
        ]
      }
    }, false)
  }

  const handleChangeSalaryDetails = (e, informationIndex, name = null) => {
    const targetName = e?.target?.name || e?.target?.id || name
    const value = e?.target?.value
    const valueObj = e?.value
    
    formik.setValues((values) => {
      const newArrayInformation = values.employee_salary_details.map((item, itemIndex) => {
        if (itemIndex == informationIndex) {
          return {
            ...item,
            [targetName]: targetName == 'is_deduction' ? valueObj : value
          };
        } else {
          return item;
        }
      });

      return {
        ...values,
        employee_salary_details: [
          ...newArrayInformation,
        ]
      }
    }, false)
  }

  const handleSelectEducation = (data, name, informationIndex) => {
    formik.setValues((values) => {
      const newArrayInformation = values.employee_educations.map((item, itemIndex) => {
        if (itemIndex == informationIndex) {
          return {
            ...item,
            [name]: name == 'country_value' ? data ? {
              label: data?.label,
              value: JSON.parse(data?.value)?.id
            } : null : data ? data : null
          };
        } else {
          return item;
        }
      });

      return {
        ...values,
        employee_educations: [
          ...newArrayInformation,
        ]
      }
    }, false)
  }

  const handleRemoveEmployeeEducation = (_, index) => {
    formik.setValues((values) => ({
      ...values,
      employee_educations: values.employee_educations.filter((_, itemIndex) => itemIndex !== index)
    }), false)
  }

  const handleRemoveEmployeeAccount = (_, index) => {
    formik.setValues((values) => ({
      ...values,
      employee_accounts: values.employee_accounts.filter((_, itemIndex) => itemIndex !== index)
    }), false)
  }

  const handleRemoveEmployeeSalaryDetails = (_, index) => {
    formik.setValues((values) => {
      const updated = values.employee_salary_details.filter((_, i) => i !== index)

      // Atur activeAccordionKey
      if (activeAccordionKey === index + 1) {
        // Jika yang dihapus adalah accordion aktif
        const nextAccordion = index + 1 <= updated.length
          ? index + 1
          : index
        setActiveAccordionKey(updated.length > 0 ? nextAccordion : null)
      } else if (activeAccordionKey > index + 1) {
        // Geser key aktif ke atas 1 posisi
        setActiveAccordionKey((prevKey) => prevKey - 1)
      }

      return {
        ...values,
        employee_salary_details: updated
      }
    }, false)
  }

  useEffect(() => {
    if (shouldScroll && lastAccordionSalaryDetailRef.current) {
      lastAccordionSalaryDetailRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      setShouldScroll(false);
    }
  }, [formik.values.employee_salary_details, shouldScroll]);
  
  return (
    <>
      {openFilePreview && (
        <AppModalFilePreview
          setOpenFilePreview={setOpenFilePreview}
          file={openFilePreview}
        />
      )}
      {
        showConfirmAddAttachments.show && (
          <AppModalConfirmation 
            confirmationType='add-attachments'
            itemName='attachments'
            itemDetails={showConfirmAddAttachments.item}
            showModal={showConfirmAddAttachments}
            setShowModal={setShowConfirmAddAttachments}
            errors={formik.errors?.attachments}
            handleChange={formik.handleChange}
            handleFileInput={handleFileInput}
            headerTitle={`Add Employee Attachments`}
          />
        )
      }
      {
        showDeleteItem.show && (
          <AppModalConfirmation 
            confirmationType='delete-item'
            itemName={`${showDeleteItem?.item?.file_original_name ? showDeleteItem?.item?.file_original_name : showDeleteItem?.item?.name}`}
            itemDetails={showDeleteItem.item}
            itemType={showDeleteItem.type}
            itemIndex={showDeleteItem?.index}
            showModal={showDeleteItem}
            setShowModal={setShowDeleteItem}
            handleConfirmation={handleRemoveFile}
          />
        )
      }
      {
        showModalConfirm.show && (
          <AppModalConfirmation 
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText="Are you sure want create a new employee?"
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      <AppCard 
        className='mb-4'
        headerTitle={<h4>Add Employee</h4>}
        bodyContent={
          <AppForm 
            values={formik.values}
            handleChange={formik.handleChange}
            errors={formik.errors}
            countryOptions={countryOptions}
            setCountry={setCountry}
            country={country}
            handleSelectCountry={handleSelectCountry}
            onDateChange={handleOnDateChange}
            genderOptions={singleGenderOptionSelect()}
            gender={gender}
            handleSelectGender={handleSelectGender}
            statusContractOptions={singleStatusContractOptionSelect()}
            statusContract={statusContract}
            handleSelectStatusContract={handleSelectStatusContract}
            degreeOptions={degreeOptions}
            projectOptions={projectOptions}
            handleAddEmployeeEducations={handleAddEmployeeEducations}
            handleChangeEducation={handleChangeEducation}
            handleSelectEducation={handleSelectEducation}
            handleRemoveEmployeeEducation={handleRemoveEmployeeEducation}
            handleSelectProject={handleSelectProject}
            projects={projects}
            handleAddEmployeeAccounts={handleAddEmployeeAccounts}
            handleChangeAccount={handleChangeAccount}
            handleRemoveEmployeeAccount={handleRemoveEmployeeAccount}
            handleAddEmployeeSalaryDetails={handleAddEmployeeSalaryDetails}
            handleChangeSalaryDetails={handleChangeSalaryDetails}
            handleRemoveEmployeeSalaryDetails={handleRemoveEmployeeSalaryDetails}
            handleRemoveImage={setShowDeleteItem}
            handleFileInput={handleImageAttachment}
            handleFileOnDrop={handleImagesOnDrop}
            setOpenFilePreview={setOpenFilePreview}
            salaryTypeOptions={singleTypeSalaryDetailOptionSelect()}
            handleCardHeaderButton={handleCardHeaderButton}
            handleConfirmDelete={setShowDeleteItem}
            lastAccordionSalaryDetailRef={lastAccordionSalaryDetailRef}
            activeAccordionKey={activeAccordionKey}
            setActiveAccordionKey={setActiveAccordionKey}
          />
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate('/employee/list')}
            onSave={() => setShowModalConfirm({ show: true })}
          />
        }
      />
    </>
  )
}

export default AddEmployee